import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IMetaData, TrendencySeoService } from '@trendency/utils';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { BaseService } from '../../../../core/components/base/base.service';
import { ApiService } from '../../../../shared/services/api.service';
import { ComponentMapService } from '../../../../shared/services/component-map.service';
import { IComponentFieldData, IFile } from '../../../../shared/shared.definitions';
import { GinopService } from '../../ginop.service';

@Component({
  selector: 'app-ginop-registration',
  templateUrl: './ginop-registration.component.html',
  styleUrls: ['./ginop-registration.component.scss'],
})
export class GinopRegistrationComponent implements OnInit, OnDestroy {
  readonly EXHIBITOR = 'exhibitor';
  readonly PARTICIPANT = 'participant';
  pageType: string;
  ginopEventId: string;
  ginopForm: UntypedFormGroup;
  image: IComponentFieldData;
  imageThumbnail: IComponentFieldData;
  formSent = false;
  participantFormFirstName: string;
  participantFormLastName: string;
  participantFormCompanyName: string;
  participantFormPosition: string;
  participantFormCheckboxTitle: string;
  participantFormButton: string;
  participantFormText1: string;
  participantFormText2: string;
  participantDocument: IFile;
  exhibitorDocument: IFile;
  isParticipantActive: boolean;
  isExhibitorActive: boolean;
  data: any;
  private readonly destroy$ = new Subject();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly apiService: ApiService,
    private readonly ginopService: GinopService,
    private readonly seo: TrendencySeoService,
    private readonly componentMapService: ComponentMapService,
    private readonly baseService: BaseService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.data.pipe(takeUntil(this.destroy$)).subscribe((res) => {
      this.pageType = this.activatedRoute.snapshot.data['pageType'];
      this.ginopEventId = this.activatedRoute.snapshot.data['data'].uuid;
      this.data = res?.['data']?.data;

      switch (this.pageType) {
        case this.EXHIBITOR:
          this.ginopForm = this.getExhibitorForm();
          break;
        case this.PARTICIPANT:
          this.ginopForm = this.getParticipantForm();
          break;
        default:
      }

      this.getActiveStatus();
      this.setDocuments();
      this.setEventContentFields();
      this.setMetaData();

      this.baseService.setSz2020Logo(
        this.componentMapService.getComponentAsObjectByType(this.data?.meta, 'Meta.GinopLogoStatus')?.ginopLogoStatus
      );
    });
  }

  onGinopFormSubmit(): void {
    if (this.ginopForm.valid) {
      this.apiService
        .getGinopRegForm$(this.pageType, this.ginopEventId)
        .pipe(
          switchMap((res) => {
            res.data = res.data.map((field: any) => {
              field.value = this.ginopForm.value[field.key];
              return field;
            });
            return this.apiService.postGinopRegistration$(this.pageType, this.ginopEventId, res);
          })
        )
        .subscribe(() => {
          this.formSent = true;
        });
    }
  }

  getExhibitorForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      companyName: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      address: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      vat: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      registryNumber: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      representative: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      contactPerson: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      position: new UntypedFormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
      countryCode: new UntypedFormControl('+', [Validators.required, Validators.minLength(3), Validators.maxLength(4)]),
      phoneNumber: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      email: new UntypedFormControl('', [Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      accept: new UntypedFormControl('', [Validators.requiredTrue]),
    });
  }

  getParticipantForm(): UntypedFormGroup {
    return new UntypedFormGroup({
      firstName: new UntypedFormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
      lastName: new UntypedFormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
      companyName: new UntypedFormControl('', [Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      position: new UntypedFormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
      email: new UntypedFormControl('', [Validators.email, Validators.required, Validators.minLength(5), Validators.maxLength(100)]),
      accept: new UntypedFormControl('', [Validators.requiredTrue]),
    });
  }

  setEventContentFields(): void {
    if (this.data?.body) {
      const components = this.data.body;
      const evenContent = components.find((component: any) => component.type === 'Body.GinopEventContent');

      if (evenContent.fields) {
        this.image = evenContent.fields.find((field: any) => field.key === 'image');
        this.imageThumbnail = evenContent.fields.find((field: any) => field.key === 'imageThumbnail');
        this.participantFormFirstName = this.ginopService.getFieldValue(evenContent.fields, 'participantFormFirstName');
        this.participantFormLastName = this.ginopService.getFieldValue(evenContent.fields, 'participantFormLastName');
        this.participantFormCompanyName = this.ginopService.getFieldValue(evenContent.fields, 'participantFormCompanyName');
        this.participantFormPosition = this.ginopService.getFieldValue(evenContent.fields, 'participantFormPosition');
        this.participantFormCheckboxTitle = this.ginopService.getFieldValue(evenContent.fields, 'participantFormCheckboxTitle');
        this.participantFormButton = this.ginopService.getFieldValue(evenContent.fields, 'participantFormButton');
        this.participantFormText1 = this.ginopService.getFieldValue(evenContent.fields, 'participantFormText1');
        this.participantFormText2 = this.ginopService.getFieldValue(evenContent.fields, 'participantFormText2');
      }
    }
  }

  updateCountryCode(): void {
    if (this.ginopForm.controls['countryCode'].value.charAt(0) !== '+') {
      this.ginopForm.controls['countryCode'].patchValue('+' + this.ginopForm.controls['countryCode'].value);
    }
  }

  setDocuments(): void {
    if (this.data?.body) {
      const ginopEventContent = this.data.body.find((content: any) => content.type === 'Body.GinopEventContent');
      if (ginopEventContent.fields) {
        const participantDocument = ginopEventContent.fields.find((field: any) => field.key === 'participantDocument');
        const exhibitorDocument = ginopEventContent.fields.find((field: any) => field.key === 'exhibitorDocument');

        if (participantDocument && participantDocument.value) {
          this.apiService.getGinopDocumentByUuid$(participantDocument.value.uuid).pipe(takeUntil(this.destroy$)).subscribe((res) => {
            this.participantDocument = res;
          });
        }

        if (exhibitorDocument && exhibitorDocument.value) {
          this.apiService.getGinopDocumentByUuid$(exhibitorDocument.value.uuid).pipe(takeUntil(this.destroy$)).subscribe((res) => {
            this.exhibitorDocument = res;
          });
        }
      }
    }
  }

  getActiveStatus(): void {
    if (this.data?.meta) {
      const metaFields = this.data.meta;
      this.isExhibitorActive = metaFields.find((item: any) => item.type === 'Meta.GinopExhibitorReg').fields[0].value;
      this.isParticipantActive = metaFields.find((item: any) => item.type === 'Meta.GinopParticipantReg').fields[0].value;
    }
  }

  setMetaData(): void {
    const metaTitle = this.componentMapService.getComponentAsObjectByType(this.data.meta, 'Meta.Title');
    const metaLead = this.componentMapService.getComponentAsObjectByType(this.data.meta, 'Meta.Lead');
    const metaThumbnail = this.componentMapService.getComponentAsObjectByType(this.data.meta, 'Meta.Thumbnail');
    const metaData: IMetaData = {
      title: metaTitle.text,
      description: metaLead.text,
      ogTitle: metaTitle.text,
      ogImage: metaThumbnail?.image?.url,
      ogImageAlt: metaThumbnail?.image?.image?.title,
    };
    this.seo.setMetaData(metaData);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
